import { logger } from './logger';

/**
 * Cache management utility for localStorage with expiration support
 */

interface CacheItem<T = unknown> {
  data: T;
  timestamp: number;
  expirationTime: number;
}

class CacheManager {
  private static instance: CacheManager;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private readonly CLEANUP_INTERVAL = 40 * 60 * 1000; // 40 minutes

  private constructor() {
    this.startPeriodicCleanup();
  }

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  /**
   * Set cache item with expiration time
   * @param key Cache key
   * @param data Data to cache
   * @param expirationTime Expiration time in milliseconds (default: 40 minutes)
   */
  public set<T>(key: string, data: T, expirationTime: number = 40 * 60 * 1000): void {
    try {
      const cacheItem: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expirationTime,
      };

      localStorage.setItem(key, JSON.stringify(cacheItem));
    } catch (error: unknown) {
      logger.error('Failed to set cache item:', error);
    }
  }

  /**
   * Get cache item if not expired
   * @param key Cache key
   * @returns Cached data or null if expired/not found
   */
  public get<T = unknown>(key: string): T | null {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) {
        return null;
      }

      const cacheItem: CacheItem<T> = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is expired
      if (now - cacheItem.timestamp > cacheItem.expirationTime) {
        this.remove(key);
        return null;
      }

      return cacheItem.data;
    } catch (error: unknown) {
      logger.error('Failed to get cache item:', error);
      this.remove(key); // Remove corrupted cache
      return null;
    }
  }

  /**
   * Check if cache item exists and is not expired
   * @param key Cache key
   * @returns True if cache exists and is valid
   */
  public has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Remove cache item
   * @param key Cache key
   */
  public remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logger.error('Failed to remove cache item:', error);
    }
  }

  /**
   * Clear all cache items
   */
  public clear(): void {
    try {
      // Get all keys that might be cache items
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            // Check if it's a cache item by looking for our structure
            if (
              parsed &&
              typeof parsed === 'object' &&
              'timestamp' in parsed &&
              'expirationTime' in parsed
            ) {
              localStorage.removeItem(key);
            }
          }
        } catch {
          // Not a JSON item, skip
        }
      });
    } catch (error) {
      logger.error('Failed to clear cache:', error);
    }
  }

  /**
   * Clean up expired cache items
   */
  public cleanupExpired(): void {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      let cleanedCount = 0;

      keys.forEach((key) => {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const parsed = JSON.parse(item);
            // Check if it's a cache item and if it's expired
            if (
              parsed &&
              typeof parsed === 'object' &&
              'timestamp' in parsed &&
              'expirationTime' in parsed
            ) {
              if (now - parsed.timestamp > parsed.expirationTime) {
                localStorage.removeItem(key);
                cleanedCount++;
              }
            }
          }
        } catch {
          // Not a JSON item or corrupted, skip
        }
      });

      if (cleanedCount > 0) {
        logger.log(`Cleaned up ${cleanedCount} expired cache items`);
      }
    } catch (error) {
      logger.error('Failed to cleanup expired cache:', error);
    }
  }

  /**
   * Get cache statistics
   */
  public getStats(): { totalItems: number; cacheItems: number; totalSize: number } {
    try {
      const keys = Object.keys(localStorage);
      let cacheItems = 0;
      let totalSize = 0;

      keys.forEach((key) => {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            totalSize += item.length;
            const parsed = JSON.parse(item);
            if (
              parsed &&
              typeof parsed === 'object' &&
              'timestamp' in parsed &&
              'expirationTime' in parsed
            ) {
              cacheItems++;
            }
          }
        } catch {
          // Not a JSON item, still count size
          const item = localStorage.getItem(key);
          if (item) {
            totalSize += item.length;
          }
        }
      });

      return {
        totalItems: keys.length,
        cacheItems,
        totalSize,
      };
    } catch (error) {
      logger.error('Failed to get cache stats:', error);
      return { totalItems: 0, cacheItems: 0, totalSize: 0 };
    }
  }

  /**
   * Start periodic cleanup of expired cache items
   */
  private startPeriodicCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanupInterval = setInterval(() => {
      this.cleanupExpired();
    }, this.CLEANUP_INTERVAL);
  }

  /**
   * Stop periodic cleanup
   */
  public destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
}

// Export singleton instance
export const cacheManager = CacheManager.getInstance();

// Export cache duration constants
export const CACHE_DURATIONS = {
  SHORT: 5 * 60 * 1000, // 5 minutes
  MEDIUM: 20 * 60 * 1000, // 20 minutes
  LONG: 40 * 60 * 1000, // 40 minutes
  VERY_LONG: 60 * 60 * 1000, // 1 hour
  DAY: 24 * 60 * 60 * 1000, // 24 hours
};

// Convenience functions for common cache operations
export const cache = {
  // Set with default 40 minutes expiration
  set: <T>(key: string, data: T, duration: number = CACHE_DURATIONS.LONG) => {
    cacheManager.set(key, data, duration);
  },

  // Get cached data
  get: <T = unknown>(key: string): T | null => {
    return cacheManager.get<T>(key);
  },

  // Check if cache exists
  has: (key: string) => {
    return cacheManager.has(key);
  },

  // Remove cache item
  remove: (key: string) => {
    cacheManager.remove(key);
  },

  // Clear all cache
  clear: () => {
    cacheManager.clear();
  },

  // Get cache stats
  stats: () => {
    return cacheManager.getStats();
  },

  // Manual cleanup
  cleanup: () => {
    cacheManager.cleanupExpired();
  },
};
